import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { Resource } from '../../common/constants/resource';
import { FetchService } from '../../common/fetch.service';
import { IUnderwriter } from './types';

interface EditPeerReviewModalProps {
  open: boolean;
  onClose: () => void;
  underwriter: IUnderwriter;
  peerReviewers: IUnderwriter[];
}

export const EditPeerReviewModal: React.FC<EditPeerReviewModalProps> = ({
  open,
  onClose,
  underwriter,
  peerReviewers,
}) => {
  const queryClient = useQueryClient();
  const [selectedPeerReviewer, setSelectedPeerReviewer] =
    useState<IUnderwriter | null>(null);

  useEffect(() => {
    // Initialize with current peer reviewer if exists
    if (underwriter.peerReviewer) {
      setSelectedPeerReviewer(underwriter.peerReviewer);
    } else {
      setSelectedPeerReviewer(null);
    }
  }, [underwriter.peerReviewer]);

  const { mutate: updatePeerReviewer, isLoading: isUpdating } = useMutation({
    mutationFn: (peerReviewerId: number | null) =>
      FetchService.getInstance().put(`/users/${underwriter.id}/peer-reviewer`, {
        body: {
          peerReviewerId,
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['underwriters'],
      });
      onClose();
    },
    onError: error => {
      console.error('Error updating peer reviewer:', error);
    },
  });

  const handleSubmit = () => {
    const peerReviewerId = selectedPeerReviewer?.id || null;
    if (peerReviewerId) {
      updatePeerReviewer(peerReviewerId);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="edit-peer-review-modal"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 550,
          bgcolor: 'background.paper',
          borderRadius: 2,
          p: 4,
        }}
      >
        <Typography variant="h6" sx={{ mb: 3 }}>
          Edit {Resource.PEER_REVIEWER} for {underwriter.displayName}
        </Typography>

        <Stack spacing={3}>
          <FormControl fullWidth>
            <InputLabel>{Resource.PEER_REVIEWER}</InputLabel>
            <Select
              value={selectedPeerReviewer?.id?.toString() || ''}
              label={Resource.PEER_REVIEWER}
              onChange={(e: SelectChangeEvent) =>
                setSelectedPeerReviewer(
                  peerReviewers.find(
                    reviewer => reviewer.id.toString() === e.target.value,
                  ) || null,
                )
              }
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {peerReviewers.map(reviewer => (
                <MenuItem key={reviewer.id} value={reviewer.id.toString()}>
                  {reviewer.displayName ||
                    `${reviewer.firstName} ${reviewer.lastName}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box
            sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}
          >
            <Button variant="outlined" onClick={onClose} disabled={isUpdating}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isUpdating}
            >
              {isUpdating ? <CircularProgress size={24} /> : 'Save Changes'}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
};
